import { regionKey } from "./LidarClearanceContext";
import type { SystemAxisLink } from "./LidarClearanceContext";
import type { LidarClearanceAxis, LidarClearanceColorScale } from "./schemas";

const MISSING_VALUE_COLOR = "#FFFFFF";

// The color scale a system's axis link actually renders with - its own
// override, if any, else the axis's own default.
export function effectiveColorScale(
  colorScaleByKey: Map<string, LidarClearanceColorScale>,
  region: string,
  entry: SystemAxisLink
): LidarClearanceColorScale | undefined {
  const scaleId = entry.link.color_scale ?? entry.axis.color_scale;
  return colorScaleByKey.get(regionKey(region, scaleId));
}

// A color scale's first stop is the sentinel color for axis value 0
// ("not present" - every axis is required to include it), not a real
// visual data point, so it's never rendered here. The remaining stops are
// gapless and 1-indexed by position, but the axis range they're previewed
// against may reach past either end - anything outside the scale's own
// coverage is filled with white rather than bleeding the nearest color
// into value territory it was never given.
export function buildGradient(
  colors: string[],
  min: number,
  max: number
): string | undefined {
  const displayColors = colors.slice(1);
  if (displayColors.length === 0) return undefined;

  const range = max - min;

  if (range === 0) {
    const color = displayColors[min - 1] ?? MISSING_VALUE_COLOR;
    return `linear-gradient(to right, ${color}, ${color})`;
  }

  const pct = (value: number) => Math.min(100, Math.max(0, ((value - min) / range) * 100));

  const parts: string[] = [];
  const firstPct = pct(1);
  if (firstPct > 0) {
    parts.push(`${MISSING_VALUE_COLOR} 0%`, `${MISSING_VALUE_COLOR} ${firstPct}%`);
  }
  displayColors.forEach((color, index) => parts.push(`${color} ${pct(index + 1)}%`));
  const lastPct = pct(displayColors.length);
  if (lastPct < 100) {
    parts.push(`${MISSING_VALUE_COLOR} ${lastPct}%`, `${MISSING_VALUE_COLOR} 100%`);
  }

  return `linear-gradient(to right, ${parts.join(", ")})`;
}

// colors[0] is the sentinel for axis value 0, and every value above that
// maps 1:1 to its own index (see buildGradient above) - a value past the
// end of the array simply has no color defined yet.
export function colorForValue(
  scale: LidarClearanceColorScale | undefined,
  value: number
): string | undefined {
  return scale?.colors[value];
}

// A color scale only has to define as many colors as the axis has possible
// values to give every value a color - fewer colors than that leaves a gap.
export function colorScaleGapWarning(
  axis: LidarClearanceAxis,
  scale: LidarClearanceColorScale | undefined,
  formatMessage: (axisValueCount: number, scaleStopCount: number) => string
): string | null {
  if (!scale) return null;
  const axisValueCount = axis.max_value - axis.min_value + 1;
  if (axisValueCount <= scale.colors.length) return null;
  return formatMessage(axisValueCount, scale.colors.length);
}
